import type { ReactElement } from "react";
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { refreshAccessToken } from "../api/client";
import { getAccessToken, getRefreshToken } from "../api/session";

/**
 * Gate for the staff screens.
 *
 * The access token lives in memory only, so a reload always starts with none —
 * this exchanges the stored refresh token for a fresh one before deciding
 * anybody needs to log in. Without it, every refresh of a tablet would bounce
 * the receptionist to a password prompt.
 */
export default function RequireAuth({ children }: { children: ReactElement }) {
  const [state, setState] = useState<"checking" | "in" | "out">(() =>
    getAccessToken() ? "in" : "checking",
  );
  const location = useLocation();

  useEffect(() => {
    if (state !== "checking") return;
    if (!getRefreshToken()) {
      setState("out");
      return;
    }
    let cancelled = false;
    void refreshAccessToken().then((ok) => {
      if (!cancelled) setState(ok ? "in" : "out");
    });
    return () => {
      cancelled = true;
    };
  }, [state]);

  if (state === "checking") return <div className="centred muted">Signing in…</div>;
  if (state === "out") return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}
